import { ReactNode } from 'react';
import { DeliveryMargin } from '../../types/delivery';
import Money from './Money';
import InfoTip from './InfoTip';
import { GlossaryKey } from './glossary';

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** `2026-09` → "September 2026"; anything unparseable is shown as sent. */
export function formatPeriod(period: string): string {
  const d = new Date(`${period}-01T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? period : MONTH.format(d);
}

function Cell({ label, glossaryKey, children, note }: {
  label: string;
  glossaryKey: GlossaryKey;
  children: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }} data-testid={`margin-${glossaryKey}`}>
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
        {label}
        <InfoTip glossaryKey={glossaryKey} />
      </div>
      <div style={{ fontSize: '1.125rem', fontWeight: 700, marginTop: '0.25rem' }}>{children}</div>
      {note && <div style={{ fontSize: '0.6875rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>{note}</div>}
    </div>
  );
}

interface MarginCardProps {
  margin: DeliveryMargin | null;
  loading: boolean;
  error: string | null;
  /**
   * True when the orders service answered 404 — i.e. it does not serve `/api/admin/delivery/margin`
   * yet. That is a deployment state, not a breakage, so it reads as a notice rather than an alarm;
   * anything else is a real failure and keeps the red banner.
   */
  unavailable?: boolean;
  onRetry: () => void;
}

/**
 * Spec 053 Flow F6 / AC-41 — month-to-date delivery margin on Finance → Configuration.
 *
 * Fees charged (net) − the cost of assigned trips. Legs that still have no driver are counted
 * beside the margin but never priced: #633 removed the default provider, so an unassigned trip
 * has no provider — and no cost — until the admin names one at assignment. Pricing them at any
 * one provider's rate would make a fact out of a guess.
 */
export default function MarginCard({ margin, loading, error, unavailable = false, onRetry }: MarginCardProps) {
  return (
    <div className="admin-card" style={{ marginTop: '1rem' }}>
      <div className="admin-card-header">
        <div>
          <h3 className="admin-card-title">Month-to-date delivery margin</h3>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
            {margin
              ? `${formatPeriod(margin.period)} · to ${margin.to} (Dubai)`
              : 'Fees Garagat charged for pickup and delivery, less what the trips cost.'}
          </div>
        </div>
      </div>

      {error && unavailable ? (
        <>
          <div className="admin-info-banner info" role="status" data-testid="margin-unavailable">
            The margin is not being reported by the orders service on this environment yet. The fee
            configuration and the provider registry above are unaffected.
          </div>
          <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={onRetry}>Try again</button>
        </>
      ) : error ? (
        <>
          <div className="admin-info-banner danger" role="alert" data-testid="margin-error">{error}</div>
          <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={onRetry}>Try again</button>
        </>
      ) : loading && !margin ? (
        <div className="admin-loading" data-testid="margin-loading"><div className="admin-loading-spinner"></div></div>
      ) : !margin ? (
        <div className="admin-empty-state" data-testid="margin-empty">
          <p>No delivery activity to report for this month yet.</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: '0.75rem' }} data-testid="margin-cells">
            <Cell label="Fees charged (net)" glossaryKey="deliveryMarginFeesNet">
              <Money amount={margin.feesChargedNet} glossaryKey="deliveryMarginFeesNet" hideTip />
            </Cell>

            <Cell
              label="Cost of assigned trips"
              glossaryKey="deliveryMarginTripCosts"
              note={
                <>
                  <Money amount={margin.assignedTripCount} glossaryKey="deliveryMarginTripCosts" format="count" hideTip />
                  {` trip${margin.assignedTripCount === 1 ? '' : 's'} assigned`}
                  {margin.assignedTripsWithoutCost > 0 && (
                    <span data-testid="assigned-without-cost">
                      {` · ${margin.assignedTripsWithoutCost} with no cost configured, not included`}
                    </span>
                  )}
                </>
              }
            >
              <Money amount={margin.assignedTripCost} glossaryKey="deliveryMarginTripCosts" hideTip />
            </Cell>

            <Cell
              label="Unassigned trips"
              glossaryKey="deliveryMarginEstimate"
              note={
                <span data-testid="estimate-unavailable">
                  Not priced — a trip&apos;s provider and cost are fixed only when its driver is assigned
                </span>
              }
            >
              <Money amount={margin.unassignedTripCount} glossaryKey="deliveryMarginEstimate" format="count" hideTip />
            </Cell>

            <Cell
              label="Margin"
              glossaryKey="deliveryMarginMtd"
              note="Fees charged (net) less assigned trip costs. Unassigned trips are not priced."
            >
              <span style={{ color: margin.margin < 0 ? 'var(--admin-danger)' : 'var(--admin-text)' }}>
                <Money amount={margin.margin} glossaryKey="deliveryMarginMtd" hideTip />
              </span>
            </Cell>
          </div>

        </>
      )}
    </div>
  );
}
